import {
  getCreatorPackageAccountConflicts,
  type CreatorCollaborationPackage
} from '@/biz/multiPlatformCollaboration/model';
import {
  capCreatorPackageRateAtChannelTotal,
  type PackageRates,
  validateCreatorPackageRate
} from '@/biz/multiPlatformCollaboration/packageRates';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { buildLoginUrl } from '@/biz/tool';
import toast from 'react-hot-toast';
import { creatorOrderV2Api } from '@/apis/creatorOrderV2/creatorOrderV2Api';
import {
  isCreatorOrderV2ApiError,
  CREATOR_ORDER_V2_ERROR_CODE
} from '@/apis/creatorOrderV2/errors';
import { OrderStatus } from '@/interface/order';
import type { InfluencerAccount } from '@/apis/influencerAccounts';
import {
  getCreatorQuoteBindingFailure,
  withCreatorQuoteBindingConflicts,
  type CreatorQuoteBindingConflict
} from '@/biz/multiPlatformCollaboration/quoteBindingConflicts';
import type { QuoteCompletionVisit } from '@/hooks/useQuoteCompletionVisit';
import {
  buildCreatorV2InitialQuoteRequest,
  buildCreatorV2QuotePreviewRequest
} from '@/biz/multiPlatformCollaboration/negotiation';
import {
  getCreatorChannelAudience,
  hasCreatorChannelAudience,
  hasCreatorIncludedAudienceData,
  isCreatorAudienceAnalysisUnsupported
} from '@/biz/multiPlatformCollaboration/audience';
import type {
  CreatorAudienceViewV2,
  CreatorMoneyRange
} from '@/interface/creatorOrderV2';
import { getCreatorPackageInitialBonusAmount } from '@/biz/multiPlatformCollaboration/dealPrice';
import { useTranslation } from 'react-i18next';
import { isScreeningAnswerValid } from '@/components/web/components/ScreeningQuestions/validation';

import {
  getCreatorAccountPickerInitialPlacementIds,
  getCreatorQuoteSelectablePlacementIds,
  withCreatorInfluencerAccounts,
  withCreatorOrderContentTypes
} from '@/biz/multiPlatformCollaboration/creatorAccountPicker';
import { ChevronStrokeIcon } from '@/components/common/icon';
import CreatorPackageAudiencePreview from '@/components/multiPlatformCollaboration/CreatorPackageAudiencePreview';
import CreatorPackagePricingDetails from '@/components/multiPlatformCollaboration/CreatorPackagePricingDetails';
import CreatorPackageQuoteEditor from '@/components/multiPlatformCollaboration/CreatorPackageQuoteEditor';
import CreatorQuoteRequirements, {
  CreatorProductGiftNotice
} from '@/components/web/components/CreatorQuoteRequirements';
import CreatorScheduleConflictSummary from '@/components/web/components/CreatorScheduleConflictSummary';
import {
  ProductPhysicalSelectEnum,
  ProductType
} from '@/components/web/config';
import { useDetailDataStore } from '@/hooks/useDetailDataStore';
import { useMultiplePricesStore } from '@/hooks/useMultiplePricesStore';
import usePackagePriceRecommendation from '@/hooks/usePackagePriceRecommendation';
import useCreatorQuotePreview from '@/hooks/useCreatorQuotePreview';
import { useWebModalStore } from '@/hooks/useWebModalStore';
import { useDashboardRefreshStore } from '@/hooks/useDashboardRefreshStore';
import {
  buildCreatorScheduleConflictPayload,
  hasSubmittedScheduleConflict,
  isCreatorScheduleConflictValid
} from '@/utils/creatorScheduleConflict';
import { scrollElementToTop } from '@/utils/scrollElementToTop';
import { getCreatorPricingContentTypeSummary } from '@/biz/multiPlatformCollaboration/contentTypePreview';
import {
  getCreatorContentTypeKey,
  getCreatorPlacementAvailableContentTypes
} from '@/biz/multiPlatformCollaboration/contentTypes';
import {
  getCreatorInitialPreOffer,
  getCreatorInitialPreOfferSelection,
  isCreatorInitialPreOfferResponseCurrent,
  type CreatorInitialPreOfferResponse
} from '@/biz/multiPlatformCollaboration/initialPreOffer';

const getInitialPlacementRates = (
  collaborationPackage: CreatorCollaborationPackage
): PackageRates =>
  Object.fromEntries(
    collaborationPackage.placements.map((placement) => [
      placement.id,
      Array.isArray(collaborationPackage.raw?.channels)
        ? 0
        : (placement.originalRate ?? placement.rate ?? 0)
    ])
  );

const getPlacementContentType = (
  placement: CreatorCollaborationPackage['placements'][number]
) =>
  String(
    placement.contentType ??
      placement.raw?.interestedPriceFields?.[0]?.contentType ??
      placement.raw?.priceFields?.[0]?.contentType ??
      ''
  );

const hasValidChannelEstimate = (
  range: CreatorMoneyRange | null | undefined
): range is CreatorMoneyRange =>
  Boolean(
    range &&
      Number.isFinite(range.min) &&
      Number.isFinite(range.max) &&
      range.min >= 0 &&
      range.max > 0 &&
      range.max >= range.min
  );

const getPlacementTotal = (
  collaborationPackage: CreatorCollaborationPackage,
  rates: PackageRates,
  includedPlacementIds: string[]
) => {
  const includedSet = new Set(includedPlacementIds);
  return (
    collaborationPackage.placements.reduce(
      (totalCents, placement) =>
        includedSet.has(placement.id)
          ? totalCents + Math.round(Number(rates[placement.id] ?? 0) * 100)
          : totalCents,
      0
    ) / 100
  );
};

function PackageQuoteBreadcrumb({ step }: { step: 1 | 2 | 3 }) {
  const { t } = useTranslation();
  const activeStepRef = useRef<HTMLSpanElement>(null);
  const items = [
    t('multi_platform.quote.mobile_step_audience', {
      defaultValue: '1. Audience demographics'
    }),
    t('multi_platform.quote.mobile_step_price', {
      defaultValue: '2. Confirm deal price'
    }),
    t('multi_platform.quote.mobile_step_submit', {
      defaultValue: '3. Submit interest'
    })
  ];

  useEffect(() => {
    activeStepRef.current?.scrollIntoView({
      block: 'nearest',
      inline: 'center'
    });
  }, [step]);

  return (
    <nav
      className="flex items-center gap-1 overflow-x-auto whitespace-nowrap pt-4 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      aria-label={t('multi_platform.quote.progress', {
        defaultValue: 'Submit interest progress'
      })}
    >
      {items.map((item, index) => (
        <div key={item} className="flex items-center gap-1">
          <span
            ref={index === step - 1 ? activeStepRef : undefined}
            className={`rounded px-2 py-1.5 text-xs font-medium ${
              index === step - 1
                ? 'bg-[#F1F3F4] text-[#292524]'
                : 'text-[#60646C]'
            }`}
            aria-current={index === step - 1 ? 'step' : undefined}
          >
            {item}
          </span>
          {index < items.length - 1 ? (
            <span
              aria-hidden
              className="flex h-4 w-4 shrink-0 items-center justify-center text-[#79716B]"
            >
              <ChevronStrokeIcon
                direction="right"
                className="h-[9px] w-[5px]"
              />
            </span>
          ) : null}
        </div>
      ))}
    </nav>
  );
}

export type CreatorPackageQuoteFlowProps = {
  collaborationPackage: CreatorCollaborationPackage;
  completionVisit: QuoteCompletionVisit;
  influencerAccounts?: InfluencerAccount[];
  initialAudienceViews?: CreatorAudienceViewV2[];
  initialPreOfferResponse?: CreatorInitialPreOfferResponse | null;
  mobile?: boolean;
  onClose: () => void;
};

function preferCompletedAudienceView(
  incoming: CreatorAudienceViewV2,
  previous?: CreatorAudienceViewV2
) {
  if (!previous) return incoming;
  const previousComplete =
    previous.source === 'first_party' ||
    (previous.source === 'self_estimated' && previous.status === 'success');
  const incomingComplete =
    incoming.source === 'first_party' ||
    (incoming.source === 'self_estimated' && incoming.status === 'success');
  if (previousComplete && !incomingComplete) return previous;
  if (
    incoming.source === 'none' &&
    previous.source === 'self_estimated' &&
    ['pending', 'processing'].includes(previous.status ?? '')
  )
    return previous;
  return incoming;
}

export default function CreatorPackageQuoteFlow({
  collaborationPackage: sourceCollaborationPackage,
  completionVisit,
  influencerAccounts,
  initialAudienceViews,
  initialPreOfferResponse,
  mobile = false,
  onClose
}: CreatorPackageQuoteFlowProps) {
  const { t } = useTranslation();
  const { isLoaded, isSignedIn } = useAuth();
  const acceptingPreOffer = initialPreOfferResponse?.mode === 'accept';
  const preOfferResponseCurrent =
    !initialPreOfferResponse ||
    isCreatorInitialPreOfferResponseCurrent(
      sourceCollaborationPackage,
      initialPreOfferResponse
    );
  const canEditChannels =
    !acceptingPreOffer &&
    sourceCollaborationPackage.raw?.status === OrderStatus.Invited;
  const showAccountSelector =
    canEditChannels && sourceCollaborationPackage.raw?.isMultiChannel === true;
  const isSingleChannelOrder =
    sourceCollaborationPackage.placements.filter(
      (placement) => placement.raw?.active !== false
    ).length === 1;
  const [bindingFailure, setBindingFailure] = useState<{
    orderId: string;
    channels: CreatorQuoteBindingConflict[];
    message?: string;
  } | null>(null);
  const baseCollaborationPackage = useMemo(
    () =>
      acceptingPreOffer
        ? sourceCollaborationPackage
        : influencerAccounts
          ? withCreatorInfluencerAccounts(
              sourceCollaborationPackage,
              influencerAccounts
            )
          : withCreatorOrderContentTypes(sourceCollaborationPackage),
    [sourceCollaborationPackage, influencerAccounts, acceptingPreOffer]
  );
  const currentBindingFailure =
    bindingFailure?.orderId === sourceCollaborationPackage.id
      ? bindingFailure
      : null;
  const collaborationPackage = useMemo(
    () =>
      withCreatorQuoteBindingConflicts(
        baseCollaborationPackage,
        currentBindingFailure?.channels ?? []
      ),
    [baseCollaborationPackage, currentBindingFailure]
  );
  const isV2ProductOrder = Array.isArray(collaborationPackage.raw?.channels);
  const {
    screeningQuestions,
    setScreeningQuestionsError,
    creatorScheduleConflict,
    creatorInitialContentTypeSelection
  } = useMultiplePricesStore();
  // The account picker may add candidates, but the legacy pre-offer belongs
  // only to the original order's single channel.
  const initialPreOffer = useMemo(
    () =>
      getCreatorInitialPreOffer(
        sourceCollaborationPackage,
        collaborationPackage.placements
      ),
    [sourceCollaborationPackage, collaborationPackage.placements]
  );
  const initialPreOfferItem = initialPreOffer
    ? getCreatorInitialPreOfferSelection(
        initialPreOffer,
        initialPreOfferResponse
          ? {
              orderId: initialPreOfferResponse.orderId,
              placementId: initialPreOffer.placement.id,
              contentType: initialPreOfferResponse.contentType
            }
          : creatorInitialContentTypeSelection
      )
    : null;
  const [storedRates, setRates] = useState<PackageRates>(() => ({
    ...getInitialPlacementRates(collaborationPackage),
    ...(initialPreOffer && initialPreOfferItem
      ? {
          [initialPreOffer.placement.id]: acceptingPreOffer
            ? initialPreOfferResponse.amount
            : initialPreOfferItem.actualGet
        }
      : {})
  }));
  const [includedPlacementIds, setIncludedPlacementIds] = useState<string[]>(
    () =>
      acceptingPreOffer
        ? sourceCollaborationPackage.placements
            .filter(
              (placement) =>
                placement.raw?.active !== false &&
                placement.channelKey === initialPreOfferResponse.channelKey
            )
            .map((placement) => placement.id)
        : getCreatorAccountPickerInitialPlacementIds(sourceCollaborationPackage)
  );
  const [sessionAudiencePlacementIds, setSessionAudiencePlacementIds] =
    useState(
      () =>
        new Set(
          collaborationPackage.placements
            .filter(
              (placement) =>
                includedPlacementIds.includes(placement.id) &&
                placement.raw?.active !== false &&
                !hasCreatorChannelAudience(
                  placement,
                  collaborationPackage,
                  initialAudienceViews
                )
            )
            .map((placement) => placement.id)
        )
    );
  const [audienceViews, setAudienceViews] = useState<
    CreatorAudienceViewV2[] | undefined
  >(() => initialAudienceViews);
  const initialAudienceReadOrderId = useRef<string | null>(null);
  const requestedAudienceKeys = useRef(new Set<string>());
  const audienceRequestsInFlight = useRef(new Set<string>());
  const [failedAudienceKeys, setFailedAudienceKeys] = useState(
    new Set<string>()
  );
  const retryAudienceAnalysis = useCallback((channelKey: string) => {
    setFailedAudienceKeys((current) => {
      const next = new Set(current);
      next.delete(channelKey);
      return next;
    });
  }, []);
  const refreshOrderAudience = useCallback(async () => {
    if (!isV2ProductOrder) return;
    const result = await creatorOrderV2Api.audience(collaborationPackage.id);
    if (
      String(result.data?.orderId) === collaborationPackage.id &&
      Array.isArray(result.data?.audienceViews)
    ) {
      setAudienceViews((current) => {
        const incoming = result.data.audienceViews;
        const keys = new Set(incoming.map((view) => view.channelKey));
        return [
          ...incoming.map((view) => {
            const previous = current?.find(
              (item) => item.channelKey === view.channelKey
            );
            return preferCompletedAudienceView(view, previous);
          }),
          ...(current ?? []).filter((view) => !keys.has(view.channelKey))
        ];
      });
    }
  }, [collaborationPackage.id, isV2ProductOrder]);
  const readChannelAudience = useCallback(async (channelKey: string) => {
    if (audienceRequestsInFlight.current.has(channelKey)) return;
    audienceRequestsInFlight.current.add(channelKey);
    try {
      const result =
        await creatorOrderV2Api.triggerAudienceSelfAnalysis(channelKey);
      const next = result.data;
      if (next?.channelKey !== channelKey)
        throw new Error('Audience analysis returned a different channel.');
      setAudienceViews((current) => [
        ...(current ?? []).filter((view) => view.channelKey !== channelKey),
        preferCompletedAudienceView(
          next,
          current?.find((view) => view.channelKey === channelKey)
        )
      ]);
      setFailedAudienceKeys((current) => {
        if (!current.has(channelKey)) return current;
        const next = new Set(current);
        next.delete(channelKey);
        return next;
      });
    } catch (error) {
      requestedAudienceKeys.current.delete(channelKey);
      setFailedAudienceKeys((current) => new Set(current).add(channelKey));
      // A failed poll must leave the loading branch so its retry is reachable.
      setAudienceViews((current) =>
        current?.map((view) =>
          view.channelKey === channelKey &&
          view.source === 'self_estimated' &&
          ['pending', 'processing'].includes(view.status ?? '')
            ? { ...view, status: 'failed' }
            : view
        )
      );
      throw error;
    } finally {
      audienceRequestsInFlight.current.delete(channelKey);
    }
  }, []);
  const refreshAudience = useCallback(
    async (pendingOnly = false) => {
      if (!isV2ProductOrder) return;
      const orderKeys = new Set(
        collaborationPackage.raw.channels.map(
          (channel: any) => channel.channelKey
        )
      );
      const selected = collaborationPackage.placements.filter((placement) => {
        if (
          !includedPlacementIds.includes(placement.id) ||
          placement.raw?.active === false
        )
          return false;
        if (!pendingOnly) return true;
        const view = getCreatorChannelAudience(
          placement,
          collaborationPackage,
          audienceViews
        );
        return (
          view?.source === 'self_estimated' &&
          ['pending', 'processing'].includes(view.status ?? '')
        );
      });
      // Unsaved quote candidates are not in GET /order/:id/audience. The channel
      // endpoint reuses an existing analysis and returns that channel's own view.
      await Promise.all([
        ...(selected.some((placement) => orderKeys.has(placement.channelKey))
          ? [refreshOrderAudience()]
          : []),
        ...selected
          .filter(
            (placement) =>
              placement.channelKey &&
              !orderKeys.has(placement.channelKey) &&
              (!pendingOnly || !failedAudienceKeys.has(placement.channelKey))
          )
          .map((placement) => readChannelAudience(placement.channelKey!))
      ]);
    },
    [
      isV2ProductOrder,
      collaborationPackage,
      includedPlacementIds,
      audienceViews,
      failedAudienceKeys,
      refreshOrderAudience,
      readChannelAudience
    ]
  );
  useEffect(() => {
    if (Array.isArray(initialAudienceViews)) {
      setAudienceViews((current) => [
        ...initialAudienceViews.map((view) => {
          const previous = current?.find(
            (item) => item.channelKey === view.channelKey
          );
          return preferCompletedAudienceView(view, previous);
        }),
        ...(current ?? []).filter(
          (view) =>
            !initialAudienceViews.some(
              (initial) => initial.channelKey === view.channelKey
            )
        )
      ]);
    }
  }, [initialAudienceViews]);
  // Completed detail data needs no request. Missing channels start analysis
  // first, then read its result; existing pending jobs are only polled.
  useEffect(() => {
    if (!isV2ProductOrder) return;
    const orderChannelKeys = new Set(
      collaborationPackage.raw.channels.map((channel: any) =>
        String(channel.channelKey ?? '')
      )
    );
    const missing = collaborationPackage.placements.filter((placement) => {
      const key = placement.channelKey;
      const audience = getCreatorChannelAudience(
        placement,
        collaborationPackage,
        audienceViews
      );
      return (
        placement.raw?.active !== false &&
        key &&
        includedPlacementIds.includes(placement.id) &&
        !requestedAudienceKeys.current.has(key) &&
        !failedAudienceKeys.has(key) &&
        !isCreatorAudienceAnalysisUnsupported(audience) &&
        !hasCreatorChannelAudience(
          placement,
          collaborationPackage,
          audienceViews
        ) &&
        !hasCreatorChannelAudience(
          placement,
          collaborationPackage,
          initialAudienceViews
        ) &&
        audience?.status !== 'pending' &&
        audience?.status !== 'processing'
      );
    });
    if (missing.length > 0) {
      initialAudienceReadOrderId.current = collaborationPackage.id;
      missing.forEach((placement) =>
        requestedAudienceKeys.current.add(placement.channelKey!)
      );
      void Promise.allSettled(
        missing.map(async (placement) => {
          const channelKey = placement.channelKey!;
          try {
            await readChannelAudience(channelKey);
          } catch (error) {
            toast.error((error as Error).message);
            throw error;
          }
        })
      ).then((results) => {
        if (
          results.some(
            (result, index) =>
              result.status === 'fulfilled' &&
              orderChannelKeys.has(missing[index].channelKey!)
          )
        )
          void refreshOrderAudience().catch((error: Error) =>
            toast.error(error.message)
          );
      });
      return;
    }
    const hasPendingAudience = collaborationPackage.placements.some(
      (placement) => {
        const audience = getCreatorChannelAudience(
          placement,
          collaborationPackage,
          audienceViews
        );
        return (
          placement.raw?.active !== false &&
          includedPlacementIds.includes(placement.id) &&
          orderChannelKeys.has(placement.channelKey ?? '') &&
          audience?.source === 'self_estimated' &&
          (audience.status === 'pending' || audience.status === 'processing')
        );
      }
    );
    if (
      hasPendingAudience &&
      initialAudienceReadOrderId.current !== collaborationPackage.id
    ) {
      initialAudienceReadOrderId.current = collaborationPackage.id;
      void refreshOrderAudience().catch((error: Error) =>
        toast.error(error.message)
      );
    }
  }, [
    collaborationPackage,
    audienceViews,
    failedAudienceKeys,
    includedPlacementIds,
    initialAudienceViews,
    isV2ProductOrder,
    refreshOrderAudience,
    readChannelAudience
  ]);
  const hasPendingAudience = collaborationPackage.placements.some(
    (placement) => {
      const audience = getCreatorChannelAudience(
        placement,
        collaborationPackage,
        audienceViews
      );
      return (
        placement.raw?.active !== false &&
        includedPlacementIds.includes(placement.id) &&
        !failedAudienceKeys.has(placement.channelKey ?? '') &&
        audience?.source === 'self_estimated' &&
        (audience.status === 'pending' || audience.status === 'processing')
      );
    }
  );
  useEffect(() => {
    if (!hasPendingAudience) return;
    const timer = window.setInterval(() => {
      void refreshAudience(true).catch(() => {
        // Keep the current audience state; retry on the next interval.
      });
    }, 3000);
    return () => window.clearInterval(timer);
  }, [hasPendingAudience, refreshAudience]);
  const initializedRatePlacementIdsRef = useRef(
    new Set<string>(initialPreOffer ? [initialPreOffer.placement.id] : [])
  );
  const pendingInitialRatePlacementIdsRef = useRef(
    new Set(
      Array.isArray(collaborationPackage.raw?.channels)
        ? includedPlacementIds.filter(
            (id) => id !== initialPreOffer?.placement.id
          )
        : []
    )
  );
  const [storedContentTypes, setSelectedContentTypes] = useState<
    Record<string, string>
  >(() => {
    // Remember defaults as well as explicit choices, so refreshed options do
    // not silently attach a previous type's rate to a new default type.
    const defaults = Object.fromEntries(
      (isV2ProductOrder ? collaborationPackage.placements : []).map(
        (placement) => [placement.id, getPlacementContentType(placement)]
      )
    );
    if (initialPreOffer && initialPreOfferItem) {
      return {
        ...defaults,
        [initialPreOffer.placement.id]: initialPreOfferItem.contentType
      };
    }
    const selection = creatorInitialContentTypeSelection;
    if (selection?.orderId !== collaborationPackage.id) return defaults;
    const placement = collaborationPackage.placements.find(
      (item) => item.id === selection.placementId
    );
    if (!placement) return defaults;
    const contentType = getCreatorPlacementAvailableContentTypes(
      placement
    ).find(
      (type) =>
        getCreatorContentTypeKey(placement.platform, type) ===
        getCreatorContentTypeKey(placement.platform, selection.contentType)
    );
    return contentType
      ? { ...defaults, [placement.id]: contentType }
      : defaults;
  });
  const { selectedContentTypes, resetRatePlacementIds } = useMemo(() => {
    let selectedContentTypes = storedContentTypes;
    const resetRatePlacementIds: string[] = [];
    // Accept mode is tied to its original offer snapshot, not refreshed defaults.
    if (!acceptingPreOffer && isV2ProductOrder) {
      collaborationPackage.placements.forEach((placement) => {
        const previous = storedContentTypes[placement.id];
        const previousKey = getCreatorContentTypeKey(
          placement.platform,
          previous || getPlacementContentType(placement)
        );
        const options = getCreatorPlacementAvailableContentTypes(placement);
        const next =
          options.find(
            (type) =>
              getCreatorContentTypeKey(placement.platform, type) === previousKey
          ) ??
          options[0] ??
          getPlacementContentType(placement);
        if (next === previous) return;
        if (selectedContentTypes === storedContentTypes) {
          selectedContentTypes = { ...storedContentTypes };
        }
        selectedContentTypes[placement.id] = next;
        // Alias-only changes (Reel/Reels) retain the user's amount.
        if (
          previous &&
          previousKey !== getCreatorContentTypeKey(placement.platform, next)
        ) {
          resetRatePlacementIds.push(placement.id);
        }
      });
    }
    return { selectedContentTypes, resetRatePlacementIds };
  }, [
    acceptingPreOffer,
    collaborationPackage.placements,
    isV2ProductOrder,
    storedContentTypes
  ]);
  const rates = useMemo(
    // All consumers use the reconciled snapshot immediately, before effects
    // persist it, so no render can preview/submit the old type or its amount.
    () =>
      resetRatePlacementIds.length
        ? {
            ...storedRates,
            ...Object.fromEntries(resetRatePlacementIds.map((id) => [id, 0]))
          }
        : storedRates,
    [resetRatePlacementIds, storedRates]
  );
  useEffect(() => {
    if (selectedContentTypes === storedContentTypes) return;
    resetRatePlacementIds.forEach((id) => {
      initializedRatePlacementIdsRef.current.delete(id);
      if (includedPlacementIds.includes(id)) {
        pendingInitialRatePlacementIdsRef.current.add(id);
      } else {
        pendingInitialRatePlacementIdsRef.current.delete(id);
      }
    });
    setSelectedContentTypes(selectedContentTypes);
    if (resetRatePlacementIds.length) {
      setRates((current) => ({
        ...current,
        ...Object.fromEntries(resetRatePlacementIds.map((id) => [id, 0]))
      }));
    }
  }, [
    includedPlacementIds,
    resetRatePlacementIds,
    selectedContentTypes,
    storedContentTypes
  ]);
  const [expandedPlacementIds, setExpandedPlacementIds] = useState<string[]>(
    () =>
      collaborationPackage.placements[0]
        ? [collaborationPackage.placements[0].id]
        : []
  );
  // This flow submits the first quote. Keep manual package pricing separate
  // from channel predictions and the order's previous price/discount.
  const [manualPackageRate, setManualPackageRate] = useState<number | null>(
    null
  );
  const [nonNegotiable, setNonNegotiable] = useState(false);
  const [message, setMessage] = useState('');
  const { detailData, reQueryDetailData } = useDetailDataStore();
  const channelErrorHandledRef = useRef(false);
  const quoteSessionActiveRef = useRef(true);
  useEffect(() => {
    quoteSessionActiveRef.current = true;
    return () => {
      quoteSessionActiveRef.current = false;
    };
  }, []);
  const handleChannelSetChanged = useCallback(async () => {
    if (
      !quoteSessionActiveRef.current ||
      channelErrorHandledRef.current ||
      !completionVisit.isCurrent()
    )
      return;
    channelErrorHandledRef.current = true;
    toast.error(
      t('multi_platform.quote.order_updated', {
        defaultValue:
          'Order information has changed. Please reopen the quote form.'
      })
    );
    onClose();
    try {
      await reQueryDetailData({ orderId: collaborationPackage.id });
    } catch {
      // The detail store owns refresh errors. Never reopen or replay the stale quote.
    }
  }, [collaborationPackage.id, completionVisit, onClose, reQueryDetailData, t]);
  const { productShippingCountry, agreeProductShip, setEducationModal } =
    useWebModalStore();
  const [selectCountryError, setSelectCountryError] = useState(false);
  const [scheduleError, setScheduleError] = useState(false);
  const scheduleSubmitted = hasSubmittedScheduleConflict(
    detailData?.order?.scheduleConflict
  );
  const isProductShip =
    detailData?.product?.productType === ProductType.PHYSICAL_PRODUCT &&
    detailData?.product?.physicalProductShipType ===
      ProductPhysicalSelectEnum.ship;
  const [showValidation, setShowValidation] = useState(false);
  const [screeningSubmissionError, setScreeningSubmissionError] = useState<
    string | null
  >(null);
  const [audienceError, setAudienceError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => setScreeningSubmissionError(null), [screeningQuestions]);
  const [mobileStep, setMobileStep] = useState<1 | 2 | 3>(() =>
    hasCreatorIncludedAudienceData(
      collaborationPackage,
      getCreatorAccountPickerInitialPlacementIds(collaborationPackage),
      audienceViews
    )
      ? 2
      : 1
  );
  const mainScrollRef = useRef<HTMLDivElement>(null);
  const [pendingBindingFocus, setPendingBindingFocus] = useState(false);
  useEffect(() => {
    if (!pendingBindingFocus || (mobile && mobileStep !== 2)) return;
    const target = mainScrollRef.current?.querySelector<HTMLElement>(
      '[data-binding-conflict="true"], [data-binding-submission-error]'
    );
    if (target) scrollElementToTop(target);
    setPendingBindingFocus(false);
  }, [pendingBindingFocus, mobile, mobileStep]);
  const [pendingAudienceFocus, setPendingAudienceFocus] = useState(false);
  useEffect(() => {
    if (!pendingAudienceFocus || (mobile && mobileStep !== 1)) return;
    // The inline errors/mobile step are mounted; the scroll helper waits for layout.
    const target = mainScrollRef.current?.querySelector<HTMLDetailsElement>(
      'details[data-audience-invalid="true"]'
    );
    if (target) {
      target.open = true;
      scrollElementToTop(target);
    }
    setPendingAudienceFocus(false);
  }, [pendingAudienceFocus, mobile, mobileStep]);
  const [pendingScreeningFocus, setPendingScreeningFocus] = useState(false);
  useEffect(() => {
    if (!pendingScreeningFocus || (mobile && mobileStep !== 2)) return;
    // Wait for the question step to mount before locating its real control.
    const frame = requestAnimationFrame(() => {
      const index = screeningQuestions.findIndex(
        (question) => !isScreeningAnswerValid(question)
      );
      const question = mainScrollRef.current?.querySelector<HTMLElement>(
        `[data-screening-question-index="${index}"]`
      );
      if (question) {
        // Do not focus the rich-text toolbar or the decline-opportunity button.
        const control = question.querySelector<HTMLElement>(
          '[contenteditable="true"], .aha-select[tabindex]'
        );
        (control ?? question).focus({ preventScroll: true });
        scrollElementToTop(question);
      }
      setPendingScreeningFocus(false);
    });
    return () => cancelAnimationFrame(frame);
  }, [pendingScreeningFocus, mobile, mobileStep, screeningQuestions]);
  const unsupportedRecommendationKeyRef = useRef('');
  const submitInFlight = useRef(false);
  const placementTotal = useMemo(
    () => getPlacementTotal(collaborationPackage, rates, includedPlacementIds),
    [collaborationPackage, rates, includedPlacementIds]
  );
  const singleChannelRateMode = includedPlacementIds.length === 1;
  const showPackageDiscount = includedPlacementIds.length > 1;
  const singleChannelPricingSummary = (() => {
    if (!singleChannelRateMode) return undefined;
    const placement = collaborationPackage.placements.find(
      (item) => item.id === includedPlacementIds[0]
    );
    if (!placement) return undefined;
    const contentType =
      selectedContentTypes[placement.id] || getPlacementContentType(placement);
    if (!contentType) return undefined;
    return getCreatorPricingContentTypeSummary({
      platform: placement.platform,
      contentType,
      t
    });
  })();
  const audienceValid = hasCreatorIncludedAudienceData(
    collaborationPackage,
    includedPlacementIds,
    audienceViews
  );
  useEffect(() => {
    const included = new Set(includedPlacementIds);
    const newlyIncludedMissingIds = collaborationPackage.placements
      .filter(
        (placement) =>
          included.has(placement.id) &&
          placement.raw?.active !== false &&
          !hasCreatorChannelAudience(
            placement,
            collaborationPackage,
            initialAudienceViews
          )
      )
      .map((placement) => placement.id);
    if (!newlyIncludedMissingIds.length) return;
    setSessionAudiencePlacementIds((current) => {
      if (newlyIncludedMissingIds.every((id) => current.has(id))) {
        return current;
      }
      return new Set([...Array.from(current), ...newlyIncludedMissingIds]);
    });
  }, [collaborationPackage, includedPlacementIds, initialAudienceViews]);
  const displayedAudiencePlacementIds = includedPlacementIds.filter((id) =>
    sessionAudiencePlacementIds.has(id)
  );
  const showSessionAudience = displayedAudiencePlacementIds.length > 0;
  useEffect(() => {
    if (audienceValid) setAudienceError(false);
  }, [audienceValid]);
  useEffect(() => {
    if (mobile && !showSessionAudience && mobileStep === 1) setMobileStep(2);
  }, [mobile, mobileStep, showSessionAudience]);
  const selectedPlatforms = useMemo(() => {
    const includedSet = new Set(includedPlacementIds);
    return new Set(
      collaborationPackage.placements
        .filter((placement) => includedSet.has(placement.id))
        .map((placement) => placement.platform)
    );
  }, [collaborationPackage.placements, includedPlacementIds]);
  const recommendationChannels = useMemo(() => {
    const includedSet = new Set(includedPlacementIds);
    return collaborationPackage.placements
      .filter((placement) => includedSet.has(placement.id))
      .map((placement) => ({
        channelKey: placement.channelKey || placement.id,
        contentType:
          selectedContentTypes[placement.id] ||
          getPlacementContentType(placement)
      }));
  }, [
    collaborationPackage.placements,
    includedPlacementIds,
    selectedContentTypes
  ]);
  const recommendation = usePackagePriceRecommendation({
    orderId: collaborationPackage.id,
    channels: recommendationChannels,
    enabled: isV2ProductOrder && !acceptingPreOffer
  });
  const hasCompleteChannelRecommendations =
    recommendationChannels.length > 0 &&
    recommendationChannels.every(({ channelKey, contentType }) => {
      const range = recommendation.recommendation?.channels.find(
        (channel) =>
          channel.channelKey === channelKey &&
          channel.contentType === contentType
      )?.estimatedRange;
      return hasValidChannelEstimate(range);
    });
  // A partial estimate is not a package quote. Keep the individual estimates
  // visible, but require a manual package rate when any selected estimate is missing.
  const automaticPackageRate =
    singleChannelRateMode ||
    !isV2ProductOrder ||
    hasCompleteChannelRecommendations
      ? placementTotal
      : 0;
  const packageRate =
    singleChannelRateMode || manualPackageRate === null
      ? automaticPackageRate
      : capCreatorPackageRateAtChannelTotal({
          packageRate: manualPackageRate,
          channelTotal: placementTotal
        });
  const packageRateValidation = validateCreatorPackageRate({
    packageRate,
    channelTotal: placementTotal
  });
  // Keep controls blocked until a successful estimate has been applied to rates.
  const applyingRecommendation =
    recommendation.status === 'success' &&
    recommendation.recommendation?.channels.some((channel) => {
      const placement = collaborationPackage.placements.find(
        (item) => (item.channelKey || item.id) === channel.channelKey
      );
      return (
        placement &&
        pendingInitialRatePlacementIdsRef.current.has(placement.id) &&
        channel.contentType ===
          (selectedContentTypes[placement.id] ||
            getPlacementContentType(placement)) &&
        hasValidChannelEstimate(channel.estimatedRange)
      );
    });
  const recommendationLoading =
    !acceptingPreOffer &&
    isV2ProductOrder &&
    (recommendation.status === 'loading' || Boolean(applyingRecommendation));
  const loadingPlacementIds = recommendationLoading
    ? includedPlacementIds.filter((id) =>
        pendingInitialRatePlacementIdsRef.current.has(id)
      )
    : [];

  const handleIncludedPlacementIdsChange = (nextPlacementIds: string[]) => {
    if (!canEditChannels || channelErrorHandledRef.current) return;
    const allowed = new Set(
      getCreatorQuoteSelectablePlacementIds(collaborationPackage)
    );
    nextPlacementIds = nextPlacementIds.filter((id) => allowed.has(id));
    const currentPlacementIds = new Set(includedPlacementIds);
    const nextPlacementIdSet = new Set(nextPlacementIds);

    nextPlacementIds.forEach((placementId) => {
      if (
        !currentPlacementIds.has(placementId) &&
        !initializedRatePlacementIdsRef.current.has(placementId)
      ) {
        pendingInitialRatePlacementIdsRef.current.add(placementId);
      }
    });
    pendingInitialRatePlacementIdsRef.current.forEach((placementId) => {
      if (!nextPlacementIdSet.has(placementId)) {
        pendingInitialRatePlacementIdsRef.current.delete(placementId);
      }
    });
    setIncludedPlacementIds(nextPlacementIds);
    if (nextPlacementIds.length <= 1) setManualPackageRate(null);
  };

  const handleRatesChange = (nextRates: PackageRates) => {
    if (acceptingPreOffer || recommendationLoading) return;
    Object.entries(nextRates).forEach(([placementId, nextRate]) => {
      if (nextRate !== rates[placementId]) {
        pendingInitialRatePlacementIdsRef.current.delete(placementId);
        initializedRatePlacementIdsRef.current.add(placementId);
      }
    });
    setRates(nextRates);
  };

  const handleContentTypesChange = (
    nextContentTypes: Record<string, string>
  ) => {
    if (acceptingPreOffer) return;
    const nextRates = { ...rates };
    collaborationPackage.placements.forEach((placement) => {
      const previous =
        selectedContentTypes[placement.id] ||
        getPlacementContentType(placement);
      const next =
        nextContentTypes[placement.id] || getPlacementContentType(placement);
      if (previous === next) return;
      const preOffer =
        placement.id === initialPreOffer?.placement.id
          ? initialPreOffer.offers.find((offer) => offer.contentType === next)
          : undefined;
      if (preOffer) {
        nextRates[placement.id] = preOffer.actualGet;
        pendingInitialRatePlacementIdsRef.current.delete(placement.id);
        initializedRatePlacementIdsRef.current.add(placement.id);
        return;
      }
      nextRates[placement.id] = 0;
      initializedRatePlacementIdsRef.current.delete(placement.id);
      pendingInitialRatePlacementIdsRef.current.add(placement.id);
    });
    setRates(nextRates);
    setSelectedContentTypes(nextContentTypes);
  };

  const handlePackageRateChange = (nextPackageRate: number) => {
    if (acceptingPreOffer || recommendationLoading) return;
    setManualPackageRate(
      capCreatorPackageRateAtChannelTotal({
        packageRate: nextPackageRate,
        channelTotal: placementTotal
      })
    );
  };

  useEffect(() => {
    if (recommendationLoading) return;
    setManualPackageRate((currentPackageRate) =>
      currentPackageRate === null
        ? null
        : capCreatorPackageRateAtChannelTotal({
            packageRate: currentPackageRate,
            channelTotal: placementTotal
          })
    );
  }, [placementTotal, recommendationLoading]);

  useEffect(() => {
    if (
      recommendation.status !== 'success' ||
      !recommendation.recommendation?.channels.length ||
      pendingInitialRatePlacementIdsRef.current.size === 0
    ) {
      return;
    }

    const placementsByChannelKey = new Map(
      collaborationPackage.placements.map((placement) => [
        placement.channelKey || placement.id,
        placement
      ])
    );
    const initialRates = new Map<string, number>();

    recommendation.recommendation.channels.forEach((channel) => {
      const placement = placementsByChannelKey.get(channel.channelKey);
      if (
        !placement ||
        !pendingInitialRatePlacementIdsRef.current.has(placement.id)
      ) {
        return;
      }
      const selectedContentType =
        selectedContentTypes[placement.id] ||
        getPlacementContentType(placement);
      if (
        channel.contentType !== selectedContentType ||
        !hasValidChannelEstimate(channel.estimatedRange)
      ) {
        return;
      }
      const { min, max } = channel.estimatedRange;
      initialRates.set(placement.id, Math.round(min + (max - min) / 2));
    });

    if (initialRates.size === 0) return;
    initialRates.forEach((_, placementId) => {
      pendingInitialRatePlacementIdsRef.current.delete(placementId);
      initializedRatePlacementIdsRef.current.add(placementId);
    });
    const nextRates = {
      ...rates,
      ...Object.fromEntries(initialRates)
    };
    setRates(nextRates);
  }, [
    collaborationPackage.placements,
    includedPlacementIds,
    rates,
    recommendation.recommendation,
    recommendation.status,
    selectedContentTypes
  ]);
  const includedConflicts = useMemo(() => {
    const includedSet = new Set(includedPlacementIds);
    return getCreatorPackageAccountConflicts(collaborationPackage).filter(
      ({ placement }) => includedSet.has(placement.id)
    );
  }, [collaborationPackage, includedPlacementIds]);
  const invalidRatePlacementIds = useMemo(
    () =>
      includedPlacementIds.filter((placementId) => {
        const rate = Number(rates[placementId]);
        return !Number.isFinite(rate) || rate <= 0;
      }),
    [includedPlacementIds, rates]
  );
  const basicRatesValid =
    includedPlacementIds.length > 0 &&
    (isV2ProductOrder || selectedPlatforms.size >= 2) &&
    invalidRatePlacementIds.length === 0 &&
    packageRateValidation.isValid;

  const quotePreviewRequest = useMemo(() => {
    if (
      !isV2ProductOrder ||
      sourceCollaborationPackage.raw.status !== OrderStatus.Invited ||
      !basicRatesValid ||
      recommendationLoading ||
      channelErrorHandledRef.current ||
      !preOfferResponseCurrent
    ) {
      return null;
    }
    try {
      return buildCreatorV2QuotePreviewRequest({
        collaborationPackage,
        rates,
        includedPlacementIds,
        amount: packageRate,
        contentTypes: selectedContentTypes
      });
    } catch {
      return null;
    }
  }, [
    basicRatesValid,
    collaborationPackage,
    includedPlacementIds,
    isV2ProductOrder,
    packageRate,
    rates,
    recommendationLoading,
    preOfferResponseCurrent,
    selectedContentTypes,
    sourceCollaborationPackage.raw.status
  ]);
  const quotePreview = useCreatorQuotePreview({
    orderId: collaborationPackage.id,
    request: quotePreviewRequest,
    enabled: isV2ProductOrder && Boolean(quotePreviewRequest)
  });
  const removedChannelPreviewError =
    isCreatorOrderV2ApiError(quotePreview.error) &&
    quotePreview.error.code ===
      CREATOR_ORDER_V2_ERROR_CODE.CHANNEL_CANNOT_BE_RE_ADDED
      ? quotePreview.error
      : null;
  useEffect(() => {
    if (removedChannelPreviewError) void handleChannelSetChanged();
  }, [removedChannelPreviewError, handleChannelSetChanged]);
  const pricingLoading =
    isV2ProductOrder &&
    Boolean(quotePreviewRequest) &&
    quotePreview.status === 'loading';
  const pricingError =
    isV2ProductOrder &&
    preOfferResponseCurrent &&
    basicRatesValid &&
    !recommendationLoading &&
    (!quotePreviewRequest || quotePreview.status === 'error');
  const pricingReady =
    !isV2ProductOrder ||
    (quotePreview.status === 'success' && Boolean(quotePreview.data));
  // Both pricing and the final request must still describe the exact offer
  // accepted in this session. A refresh never silently opts into a newer price.
  const acceptedQuoteMatches =
    !acceptingPreOffer ||
    (quotePreviewRequest?.channels.length === 1 &&
      quotePreviewRequest.channels[0].channelKey ===
        initialPreOfferResponse.channelKey &&
      quotePreviewRequest.channels[0].contentType ===
        initialPreOfferResponse.contentType &&
      quotePreviewRequest.channels[0].amount ===
        initialPreOfferResponse.amount &&
      quotePreviewRequest.amount === initialPreOfferResponse.amount &&
      (!pricingReady ||
        quotePreview.data?.amount === initialPreOfferResponse.amount));
  const preOfferResponseInvalid =
    !preOfferResponseCurrent || !acceptedQuoteMatches;

  useEffect(() => {
    if (recommendation.status !== 'unsupported') return;
    const requestKey = JSON.stringify(recommendationChannels);
    if (unsupportedRecommendationKeyRef.current === requestKey) return;
    unsupportedRecommendationKeyRef.current = requestKey;
    toast.error(
      t('multi_platform.quote.unsupported_content_type', {
        defaultValue:
          'This content type is unavailable for package pricing. Please select another option.'
      })
    );
    void useDetailDataStore
      .getState()
      .reQueryDetailData({ orderId: collaborationPackage.id });
  }, [
    collaborationPackage.id,
    recommendation.status,
    recommendationChannels,
    t
  ]);

  const scrollToValidationTarget = () => {
    requestAnimationFrame(() => {
      const root = mainScrollRef.current;
      const target = includedConflicts.length
        ? root?.querySelector(
            `[data-placement-id="${includedConflicts[0].placement.id}"]`
          )
        : invalidRatePlacementIds.length
          ? root?.querySelector(
              `[data-placement-id="${invalidRatePlacementIds[0]}"]`
            )
          : !packageRateValidation.isValid
            ? root?.querySelector('[data-package-rate]')
            : root?.querySelector('[data-package-audience]');
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const scrollToScreeningTarget = (selector: string) => {
    requestAnimationFrame(() => {
      const root = mainScrollRef.current;
      const target = root?.querySelector(selector);
      if (target instanceof HTMLElement) scrollElementToTop(target);
    });
  };

  const showAudienceValidation = () => {
    setAudienceError(true);
    if (mobile) setMobileStep(1);
    setPendingAudienceFocus(true);
  };

  const validateSubmission = () => {
    if (preOfferResponseInvalid) return false;
    if (!audienceValid) {
      showAudienceValidation();
      return false;
    }
    const invalidAnswers = screeningQuestions.some(
      (question) => !isScreeningAnswerValid(question)
    );
    const invalidCountry =
      isProductShip && (!productShippingCountry || !agreeProductShip);
    const invalidSchedule =
      !scheduleSubmitted &&
      !isCreatorScheduleConflictValid(creatorScheduleConflict);
    setScreeningQuestionsError(invalidAnswers);
    setSelectCountryError(invalidCountry);
    setScheduleError(invalidSchedule);
    if (invalidAnswers || invalidCountry || invalidSchedule) {
      setShowValidation(true);
      if (mobile) setMobileStep(2);
      if (invalidAnswers) {
        setPendingScreeningFocus(true);
        return false;
      }
      scrollToScreeningTarget(
        invalidCountry
          ? '#take-order-shipping-country'
          : '#take-order-schedule-conflict'
      );
      return false;
    }
    const valid = basicRatesValid && !includedConflicts.length;
    if (valid) return true;
    const firstInvalidPlacementId =
      includedConflicts[0]?.placement.id ?? invalidRatePlacementIds[0];
    if (firstInvalidPlacementId) {
      setExpandedPlacementIds([firstInvalidPlacementId]);
    }
    setShowValidation(true);
    if (mobile) setMobileStep(2);
    scrollToValidationTarget();
    return false;
  };

  const submit = async () => {
    if (!isLoaded || submitInFlight.current) return;
    if (!isSignedIn) {
      window.location.href = buildLoginUrl({
        orderId: collaborationPackage.id
      });
      return;
    }
    if (
      (isV2ProductOrder &&
        sourceCollaborationPackage.raw.status !== OrderStatus.Invited) ||
      recommendationLoading ||
      channelErrorHandledRef.current ||
      removedChannelPreviewError ||
      !validateSubmission()
    )
      return;
    if (!pricingReady) return;
    submitInFlight.current = true;
    const submittedPath = window.location.pathname;
    setScreeningSubmissionError(null);
    setBindingFailure((current) => (current?.message ? null : current));
    setSubmitting(true);
    try {
      const request = buildCreatorV2InitialQuoteRequest({
        collaborationPackage,
        rates,
        includedPlacementIds,
        amount: packageRate,
        nonNegotiable: acceptingPreOffer ? undefined : nonNegotiable,
        message,
        contentTypes: selectedContentTypes,
        productShippingCountry,
        screeningQuestions,
        scheduleConflict: scheduleSubmitted
          ? undefined
          : buildCreatorScheduleConflictPayload(creatorScheduleConflict)
      });
      const response = await creatorOrderV2Api.join(
        collaborationPackage.id,
        request
      );
      // Only the historical join response defines the onboarding flag.
      // order.isFirstOrder is a pricing benefit, not an onboarding signal.
      const showOnboarding =
        response.data &&
        'isFirstOrder' in response.data &&
        response.data.isFirstOrder === true;
      toast.success(
        t('modal.not_interest.feedback.success', {
          defaultValue: 'Submission success.'
        })
      );
      onClose();
      useDashboardRefreshStore.getState().requestRefresh();
      let refreshedDetail;
      try {
        refreshedDetail = await reQueryDetailData({
          orderId: collaborationPackage.id
        });
      } catch {
        toast.error(
          t('multi_platform.quote.refresh_failed', {
            defaultValue:
              'Your quote was submitted. Please refresh the order to see the latest status.'
          })
        );
      }
      if (
        !completionVisit.isCurrent() ||
        window.location.pathname !== submittedPath
      )
        return;
      if (showOnboarding) {
        setEducationModal(true, refreshedDetail || detailData);
      } else if (!(refreshedDetail || detailData)?.payment) {
        // Match main's success-message delay. Closing the quote drawer must not
        // cancel this navigation, but leaving the order must not redirect later.
        completionVisit.schedule(() => {
          if (window.location.pathname === submittedPath) {
            window.location.href = `/campaign/wallet/${encodeURIComponent(collaborationPackage.id)}`;
          }
        }, 2000);
      }
    } catch (error) {
      const bindingError = getCreatorQuoteBindingFailure(error);
      if (bindingError) {
        const conflicts = collaborationPackage.placements.filter(
          (placement) =>
            includedPlacementIds.includes(placement.id) &&
            bindingError.channels.some(
              (channel) => channel.channelKey === placement.channelKey
            )
        );
        setBindingFailure({
          orderId: collaborationPackage.id,
          channels: bindingError.channels.filter((channel) =>
            conflicts.some(
              (placement) => placement.channelKey === channel.channelKey
            )
          ),
          message: conflicts.length
            ? undefined
            : error instanceof Error
              ? error.message
              : undefined
        });
        setShowValidation(true);
        if (conflicts.length) setExpandedPlacementIds([conflicts[0].id]);
        if (mobile) setMobileStep(2);
        setPendingBindingFocus(true);
        return;
      }
      if (
        isCreatorOrderV2ApiError(error) &&
        error.code === CREATOR_ORDER_V2_ERROR_CODE.CHANNEL_CANNOT_BE_RE_ADDED
      ) {
        await handleChannelSetChanged();
        return;
      }
      if (
        error instanceof Error &&
        screeningQuestions.length > 0 &&
        /answer.*brand.*requirements/i.test(error.message)
      ) {
        setScreeningSubmissionError(error.message);
        if (mobile) setMobileStep(2);
        scrollToScreeningTarget('[data-screening-submission-error]');
      }
      toast.error(
        error instanceof Error
          ? error.message
          : t('modal.not_interest.feedback.failed', {
              defaultValue: 'Submission failed.'
            })
      );
      if (
        isCreatorOrderV2ApiError(error) &&
        error.recoveryAction === 'REFRESH_WITHOUT_REPLAY'
      ) {
        if (error.cause === 'APPLICATION_NOT_ALLOWED') {
          onClose();
        }
        try {
          await reQueryDetailData({ orderId: collaborationPackage.id });
        } catch {
          /* Keep entered values for manual recovery. */
        }
      }
    } finally {
      submitInFlight.current = false;
      setSubmitting(false);
    }
  };

  const preOfferResponseNotice = preOfferResponseInvalid ? (
    <p role="alert" className="mb-4 text-sm text-[#B91C1C]">
      {t('multi_platform.quote.pre_offer_session_changed', {
        defaultValue:
          'This offer has changed or is no longer available. Close this form and review the latest offer before continuing.'
      })}
    </p>
  ) : null;
  const editor = (
    <>
      <CreatorProductGiftNotice />
      {!mobile ? preOfferResponseNotice : null}
      <CreatorPackageQuoteEditor
        collaborationPackage={collaborationPackage}
        mobile={mobile}
        quoteTermsReadOnly={acceptingPreOffer}
        initializeFromRecommendation={isV2ProductOrder}
        recommendationLoading={recommendationLoading}
        loadingPlacementIds={loadingPlacementIds}
        singleChannelRateMode={singleChannelRateMode}
        rates={rates}
        includedPlacementIds={includedPlacementIds}
        expandedPlacementIds={expandedPlacementIds}
        selectedContentTypes={selectedContentTypes}
        channelRecommendations={
          isV2ProductOrder
            ? (recommendation.recommendation?.channels ?? [])
            : undefined
        }
        showConflictValidation={showValidation}
        invalidRatePlacementIds={
          showValidation ? invalidRatePlacementIds : undefined
        }
        // Multi-channel quotes keep account management available even with no
        // selectable channels, so users can still reach Add account.
        showAccountSelector={showAccountSelector}
        showAccountsTitle={!isSingleChannelOrder || showAccountSelector}
        allowRemove={canEditChannels && includedPlacementIds.length > 1}
        onRatesChange={handleRatesChange}
        onIncludedPlacementIdsChange={handleIncludedPlacementIdsChange}
        onExpandedPlacementIdsChange={setExpandedPlacementIds}
        onSelectedContentTypesChange={handleContentTypesChange}
        placementTotal={placementTotal}
        packageRate={packageRate}
        recommendationRange={
          isV2ProductOrder
            ? hasCompleteChannelRecommendations
              ? recommendation.range
              : null
            : undefined
        }
        packageRateValidation={
          showValidation ? packageRateValidation : undefined
        }
        nonNegotiable={nonNegotiable}
        message={message}
        onPackageRateChange={handlePackageRateChange}
        onNonNegotiableChange={(value) => {
          if (!acceptingPreOffer) setNonNegotiable(value);
        }}
        onMessageChange={setMessage}
      />
      {currentBindingFailure?.message ? (
        <p
          role="alert"
          data-binding-submission-error
          className="mt-2 text-xs leading-[18px] text-[#DC2626]"
        >
          {currentBindingFailure.message}
        </p>
      ) : null}
      <div className="mt-6">
        <CreatorQuoteRequirements
          selectCountryError={selectCountryError}
          setSelectCountryError={setSelectCountryError}
          scheduleError={scheduleError}
          screeningSubmissionError={screeningSubmissionError}
        />
      </div>
      {!mobile && showSessionAudience ? (
        <div data-package-audience>
          <CreatorPackageAudiencePreview
            collaborationPackage={collaborationPackage}
            includedPlacementIds={displayedAudiencePlacementIds}
            audienceViews={audienceViews}
            onRefreshAudience={refreshAudience}
            failedAudienceKeys={failedAudienceKeys}
            onRetryAudienceAnalysis={retryAudienceAnalysis}
            audienceError={audienceError}
          />
        </div>
      ) : null}
    </>
  );

  if (mobile) {
    return (
      <div className="flex min-h-full flex-col bg-white">
        <div>
          <PackageQuoteBreadcrumb step={mobileStep} />
        </div>
        {preOfferResponseNotice}

        {mobileStep === 1 ? (
          <div
            ref={mainScrollRef}
            className="flex-1 pb-6 pt-6"
            data-package-audience
          >
            <CreatorPackageAudiencePreview
              collaborationPackage={collaborationPackage}
              includedPlacementIds={displayedAudiencePlacementIds}
              audienceViews={audienceViews}
              onRefreshAudience={refreshAudience}
              failedAudienceKeys={failedAudienceKeys}
              onRetryAudienceAnalysis={retryAudienceAnalysis}
              audienceError={audienceError}
            />
          </div>
        ) : mobileStep === 2 ? (
          <div ref={mainScrollRef} className="flex-1 pb-6 pt-6">
            {editor}
          </div>
        ) : (
          <div className="flex-1 pb-6 pt-6">
            <CreatorPackagePricingDetails
              freeProduct={
                isProductShip
                  ? { value: detailData?.product?.physicalProductCashValue }
                  : undefined
              }
              recommendationLoading={recommendationLoading}
              pricingPreview={quotePreview.data}
              pricingLoading={pricingLoading}
              pricingError={pricingError}
              onRetryPricing={
                quotePreviewRequest ? quotePreview.retry : undefined
              }
              requirementsSummary={<CreatorScheduleConflictSummary />}
              bonusAmount={getCreatorPackageInitialBonusAmount(
                collaborationPackage
              )}
              previewPricing={!isV2ProductOrder}
              placementTotal={placementTotal}
              packageRate={packageRate}
              showPackageDiscount={showPackageDiscount}
              singleChannelSummary={singleChannelPricingSummary}
            />
          </div>
        )}

        <div className="sticky bottom-0 z-10 grid grid-cols-2 gap-[13px] bg-white pb-[calc(8px+env(safe-area-inset-bottom,0px))] pt-4">
          <button
            type="button"
            className="h-10 rounded-md border border-[#D6D3D1] bg-white px-4 text-sm font-semibold text-[#44403C]"
            onClick={() => {
              if (mobileStep === 3) {
                setMobileStep(2);
              } else if (mobileStep === 2) {
                if (showSessionAudience) setMobileStep(1);
                else onClose();
              } else {
                onClose();
              }
            }}
          >
            {t('general_text.back', { defaultValue: 'Back' })}
          </button>
          <button
            type="button"
            disabled={
              submitting ||
              preOfferResponseInvalid ||
              (mobileStep !== 1 &&
                (recommendationLoading || (mobileStep === 3 && !pricingReady)))
            }
            className="h-10 rounded-md border-0 bg-[#FF5A1F] px-4 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#D7D3D0]"
            onClick={() => {
              if (mobileStep === 1) {
                if (audienceValid) {
                  setAudienceError(false);
                  setMobileStep(2);
                } else {
                  showAudienceValidation();
                }
              } else if (mobileStep === 2) {
                if (validateSubmission()) setMobileStep(3);
              } else {
                submit();
              }
            }}
          >
            {mobileStep !== 3
              ? t('general_text.continue', { defaultValue: 'Continue' })
              : submitting
                ? t('multi_platform.quote.submitting', {
                    defaultValue: 'Submitting...'
                  })
                : t('multi_platform.quote.submit_interest', {
                    defaultValue: 'Submit interest'
                  })}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 overflow-hidden">
      <main
        ref={mainScrollRef}
        className="w-[780px] flex-none overflow-y-auto px-6 pb-6 pt-6"
      >
        {editor}
      </main>
      <aside className="w-[420px] flex-shrink-0 border-l border-[#E7E5E4] px-6 py-6">
        <CreatorPackagePricingDetails
          freeProduct={
            isProductShip
              ? { value: detailData?.product?.physicalProductCashValue }
              : undefined
          }
          recommendationLoading={recommendationLoading}
          pricingPreview={quotePreview.data}
          pricingLoading={pricingLoading}
          pricingError={pricingError}
          onRetryPricing={quotePreviewRequest ? quotePreview.retry : undefined}
          requirementsSummary={<CreatorScheduleConflictSummary />}
          bonusAmount={getCreatorPackageInitialBonusAmount(
            collaborationPackage
          )}
          previewPricing={!isV2ProductOrder}
          placementTotal={placementTotal}
          packageRate={packageRate}
          showPackageDiscount={showPackageDiscount}
          singleChannelSummary={singleChannelPricingSummary}
          showActions
          submitLabel={t('multi_platform.quote.submit_interest', {
            defaultValue: 'Submit interest'
          })}
          submitting={submitting}
          submitDisabled={
            submitting ||
            preOfferResponseInvalid ||
            recommendationLoading ||
            (basicRatesValid && !pricingReady)
          }
          onSubmit={submit}
          onCancel={onClose}
        />
      </aside>
    </div>
  );
}
